import { useMemo, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { SegmentedFilter } from "@/components/admin/segmented-filter";
import { UserDeleteDialog } from "@/components/admin/user-delete-dialog";
import { ROLE_LABELS, UserFormDialog } from "@/components/admin/user-form-dialog";
import { useAdminView } from "@shared/client/admin-view";
import { Link } from "@shared/client/navigation";
import { usePageTitle } from "@shared/client/view";
import { AdminEmpty, AdminMeta, AdminPanel, StatusDot } from "@shared/components/blocks";
import { Avatar, AvatarFallback } from "@shared/components/ui/avatar";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import type { AdminUserRecord } from "@shared/lib/admin-users";
import { formatDateTime } from "@shared/lib/format";

type UsersData = {
  users: AdminUserRecord[];
  clients: Array<{ id: string; name: string }>;
  currentUserId: string | null;
};

const displayName = (user: AdminUserRecord) => user.full_name.trim() || user.email;

function initials(user: AdminUserRecord) {
  const letters = [user.first_name, user.last_name].map((part) => part.trim().charAt(0)).join("");
  return (letters || user.email.charAt(0)).toUpperCase();
}

const FILTERS: Array<{ key: string; label: string; match: (user: AdminUserRecord) => boolean }> = [
  { key: "all", label: "Tous", match: () => true },
  { key: "admin", label: "Admins", match: (user) => user.is_admin },
  { key: "paying", label: "Abonnés", match: (user) => user.paying },
  { key: "onboarding", label: "Onboarding incomplet", match: (user) => !user.onboarding_completed },
  { key: "no-client", label: "Sans client", match: (user) => user.access.length === 0 },
];

export default function AdminUsersPage() {
  usePageTitle("Utilisateurs — Administration Hune");
  const { data, error: loadError } = useAdminView<UsersData>("/api/views/admin/users");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  // null : fermé ; "new" : création ; sinon l'identifiant du compte modifié.
  const [editing, setEditing] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const users = useMemo(() => data?.users ?? [], [data]);
  const filters = useMemo(
    () => FILTERS.map((item) => ({ key: item.key, label: item.label, count: users.filter(item.match).length })),
    [users],
  );
  const shown = useMemo(() => {
    const match = FILTERS.find((item) => item.key === filter)?.match ?? (() => true);
    const term = search.trim().toLowerCase();
    return users.filter(
      (user) =>
        match(user) &&
        (!term ||
          [user.email, user.full_name, ...user.access.map((item) => item.client_name)].some((value) =>
            value.toLowerCase().includes(term),
          )),
    );
  }, [users, filter, search]);

  if (loadError) return <AdminDataNotice error={loadError} />;
  if (!data) return null;

  const editedUser = editing && editing !== "new" ? (users.find((user) => user.id === editing) ?? null) : null;
  const deletingUser = users.find((user) => user.id === deletingId) ?? null;
  const onboarded = users.filter((user) => user.onboarding_completed).length;

  return (
    <>
      <AdminMeta note={`${onboarded} onboardé${onboarded > 1 ? "s" : ""} sur ${users.length}`}>
        Comptes, droits d’administration et accès aux clients
      </AdminMeta>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setEditing("new")}>
          <Plus /> Ajouter un utilisateur
        </Button>
        <div className="relative w-full sm:w-72">
          <Search aria-hidden="true" className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Rechercher un utilisateur"
            className="h-9 pl-9"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher…"
            value={search}
          />
        </div>
        <SegmentedFilter items={filters} label="Filtrer les utilisateurs" onChange={setFilter} value={filter} />
      </div>

      <AdminPanel className="mt-3" flush>
        {shown.length === 0 ? (
          <AdminEmpty>{users.length === 0 ? "Aucun utilisateur inscrit pour le moment." : "Aucun utilisateur ne correspond."}</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Utilisateur</TableHead>
                <TableHead className="max-md:hidden">Rôles</TableHead>
                <TableHead className="max-sm:hidden">Plan</TableHead>
                <TableHead className="max-lg:hidden">Dernière connexion</TableHead>
                <TableHead className="pr-4 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="pl-5">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar className="size-9 max-sm:hidden">
                        <AvatarFallback className="bg-muted text-[12.5px] font-medium text-muted-foreground">{initials(user)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <span className="block max-w-[300px] truncate font-semibold">
                          {displayName(user)}
                          {user.id === data.currentUserId && <span className="font-normal text-muted-foreground"> · vous</span>}
                        </span>
                        <span className="block max-w-[300px] truncate text-[12.5px] text-muted-foreground">{user.email}</span>
                        {(user.job_title || user.acquisition_source) && (
                          <span className="block max-w-[300px] truncate text-[12.5px] text-muted-foreground">
                            {[user.job_title, user.acquisition_source && `Connu par : ${user.acquisition_source}`].filter(Boolean).join(" · ")}
                          </span>
                        )}
                        <span className="mt-0.5 block text-[12.5px] text-muted-foreground md:hidden">
                          {user.is_admin ? "Admin" : user.plan_name}
                          {user.access.length > 0 &&
                            ` · ${user.access.map((item) => `${item.client_name} (${ROLE_LABELS[item.role] ?? item.role})`).join(", ")}`}
                        </span>
                        {!user.onboarding_completed && (
                          <StatusDot className="mt-0.5" tone="lead">Onboarding incomplet</StatusDot>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="max-md:hidden">
                    <span className="flex max-w-[420px] flex-wrap gap-1.5">
                      {user.is_admin && <Badge variant="secondary">Administrateur global</Badge>}
                      {user.access.map((item) => (
                        <Badge asChild key={item.client_id} variant="outline">
                          <Link href={`/admin/clients/${item.client_id}`}>
                            {item.client_name} · {ROLE_LABELS[item.role] ?? item.role}
                          </Link>
                        </Badge>
                      ))}
                      {!user.is_admin && user.access.length === 0 && (
                        <span className="text-[13px] text-muted-foreground">Aucun rôle défini</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="max-sm:hidden">
                    <span>{user.is_admin ? "Admin" : user.plan_name}</span>
                    <span className="block text-[12.5px] text-muted-foreground">
                      {user.offers} offre{user.offers > 1 ? "s" : ""}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums max-lg:hidden">
                    {user.last_sign_in_at ? formatDateTime(user.last_sign_in_at) : "Jamais"}
                  </TableCell>
                  <TableCell className="pr-3 text-right whitespace-nowrap">
                    <Button aria-label={`Modifier ${displayName(user)}`} onClick={() => setEditing(user.id)} size="icon" variant="ghost">
                      <Pencil className="text-muted-foreground" />
                    </Button>
                    <Button aria-label={`Supprimer ${displayName(user)}`} onClick={() => setDeletingId(user.id)} size="icon" variant="ghost">
                      <Trash2 className="text-muted-foreground" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminPanel>

      <UserFormDialog
        clients={data.clients}
        currentUserId={data.currentUserId}
        onClose={() => setEditing(null)}
        open={editing !== null}
        user={editedUser}
      />
      <UserDeleteDialog currentUserId={data.currentUserId} onClose={() => setDeletingId(null)} user={deletingUser} />
    </>
  );
}
